import React, { forwardRef, useImperativeHandle, useRef } from "react"
import { useState, useEffect } from "react"
import { useRouter } from "next/router"
import _get from "lodash/get"
import { useDispatch, useSelector } from "react-redux"
import TooltipIcon from "@/public/icons/tooltip-questionmark.svg"
import Input from "@/components/core/Input/Input"
import Button from "@/components/core/Button/Button"
import { OrderSummaryItem } from "@/components/cart/OrderSummary/OrderSummaryItem"
import {
  ESTIMATED_TOTAL_FOR_NO_COST_EMI,
  maxAppReached,
  stopedByPreviousDiscount,
} from "@/constants/index"
import { calculateCartData, isPromoAllowed } from "@/utils/helper"
import { isNullOrUndefined } from "@/utils/index"
import OrderReview from "@/components/checkout/OrderReview"
import { cartPage } from "@/constants/index"
import {
  selectCartState,
  setCardStatus,
  setCart,
} from "@/store/features/cartSlice"
import { clearCartFromStore, getTotalInclusiveTax } from "../cartHelper"
import {
  addAnalyticsEditCart,
  continueCheckoutEventInfo,
} from "@/components/cart/analytics"
import { selectAuthState } from "@/store/features/authSlice"
import Modal from "@/components/core/Modal/Modal"
import { NumericFormat } from "react-number-format"
import { removeCustomerCartId } from "@/utils/cart"
import Collapsible from "@/components/core/Collapsible"

const OrderSummary = forwardRef((props, ref) => {
  const {
    subtotal,
    MRPsubtotal,
    totalPromotions,
    estimatedTax,
    estimatedTotal,
    promoCodes = [],
    i18n = {},
    checkoutMode = "",
    shippingDone = "",
    status,
    toastError,
    dispatchCallback,
    cartItems = [],
    disableCheckout,
    checkoutUrl,
    currencySign,
    oldPromoCodes,
    oldDiscountCodes,
    shippingAllTotal = [],
    legalLinks = {},
    isNoTooltip,
    isAbandonedCart = false,
    inventoryCheck,
    abandonedFlag = false,
    handleAbandonedCarts = () => {},
    loadAbandonedCart,
    couponPromotions,
    productPromotions,
    redirectCallback,
    isPaymentStep = false,
    isCheckoutPage = false,
    setStickyCheckout,
    checkoutSeen,
    setCheckoutSeen,
    promoCodesList = [],
    promo = "",
    setPromo = () => {},
  } = props

  const { cart: activeCart = {} } = useSelector(selectCartState)
  const { shippingAddress: { streetName = "" } = {} } = activeCart
  const dispatch = useDispatch()
  // const { t } = useTranslation("common")
  // const staticText = getTranslations(t)
  // const { freeShipping = "" } = staticText
  const { user: { persona = [], sub = "" } = {} } = useSelector(selectAuthState)

  const isIscUser = persona?.includes("IN_Kohler_ISC")
  const router = useRouter()

  const [promoCode, setPromoCode] = useState("")
  const [promoError, setPromoError] = useState("")
  const [oldPromoCode, setOldPromoCode] = useState([])
  const [oldDiscountCode, setOldDiscountCode] = useState([])
  const [taxInformation, setTaxInformation] = useState(0.0)
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [promoLoading, setPromoLoading] = useState(null)

  const CHECKOUT_BUTTON_LABEL = isAbandonedCart
    ? i18n.checkoutAsCustomer
    : i18n.checkout

  useEffect(() => {
    const taxValue = getTotalInclusiveTax()
    if (!isNullOrUndefined(taxValue) && !isNaN(Number(taxValue))) {
      const formattedTaxValue = ((Number(taxValue) * 100) / 100).toFixed(2)
      setTaxInformation(formattedTaxValue)
    }
  }, [activeCart.lineItems])
  const buttonRef = useRef()

  useEffect(() => {
    const targetElement = buttonRef.current
    if (!targetElement) {
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setStickyCheckout(!entry.isIntersecting)
      },
      { root: null, rootMargin: "0px", threshold: 0.1 }
    )
    if (buttonRef.current) {
      observer.observe(buttonRef.current)
    }
    return () => {
      if (buttonRef.current) {
        observer.unobserve(buttonRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (promoCodes?.length) {
      setPromoError("")
      setPromoCode("")
    }
  }, [promoCodes])

  useEffect(() => {
    if (promoError) document.getElementById("kf-cart-promo-error")?.focus()
  }, [promoError])

  useEffect(() => {
    setOldPromoCode(oldPromoCodes)
  }, [oldPromoCodes])

  useEffect(() => {
    setOldDiscountCode(oldDiscountCodes)
  }, [oldDiscountCodes])

  const handleAddDiscount = (promo = null, fromList = false) => {
    const code = fromList ? promo : promo.target.value || promoCode.trim()
    const payload = {
      cartID: activeCart.id,
      actions: [
        {
          code: code,
          action: "addDiscountCode",
        },
      ],
    }
    code
      ? dispatchCallback("discount", payload)
          .unwrap()
          .then(function (rsp) {
            if (rsp?.error) {
              if (
                rsp?.error?.includes("was not found") ||
                rsp?.error?.includes(
                  "does not contain active and valid cart discounts"
                )
              ) {
                setPromoError(i18n?.invalidPromoErr)
              } else if (rsp?.error?.includes("valid only")) {
                setPromoError(i18n?.expirePromoErr)
              } else if (rsp?.error?.includes("Only one")) {
                setPromoError(rsp?.error)
              } else {
                setPromoError(rsp?.error)
              }
            } else {
              if (promoCode || code) {
                !fromList ? setPromo(promoCode) : setPromo(code)
              }
              handleDiscountResponse(
                rsp,
                oldPromoCode,
                oldDiscountCode,
                promoCode || code,
                fromList
              )
            }
          })
          .catch(er => {
            updatePromoAnalytics(
              i18n?.invalidPromoErr ?? "",
              undefined,
              "",
              "apply"
            )
            setPromoError(i18n?.invalidPromoErr ?? "")
          })
          .finally(() => {
            setPromoLoading(null)
          })
      : setPromoError(i18n?.noPromoErr ?? "")
    !code &&
      updatePromoAnalytics(i18n?.noPromoErr ?? "", undefined, "", "apply")
  }

  const handleRemoveDiscount = (Id, code, codeApplied = "") => {
    const payload = {
      cartID: activeCart.id,
      actions: [
        {
          action: "removeDiscountCode",
          discountCode: {
            typeId: "discount-code",
            id: Id,
          },
        },
      ],
    }
    Id
      ? dispatchCallback("discount", payload)
          .unwrap()
          .then(async resp => {
            await updatePromoAnalytics("success", resp, code, "remove")
            if (isIscUser) {
              dispatch(setCart(resp))
            } else {
              dispatchCallback("taxCalculation")
            }
            setPromoError("")
            if (!codeApplied) {
              setPromo("")
              setPromoLoading(null)
            } else {
              handleAddDiscount(codeApplied, true)
            }
          })
          .catch(async () => {
            await updatePromoAnalytics(
              "failed to remove discount code",
              undefined,
              code,
              "remove"
            )
            toastError(i18n.removeDiscountErrorMsg)
            setPromoLoading(null)
          })
      : null
  }

  const handleDiscountResponse = (
    payload,
    oldPromoCodePassed,
    oldDiscountCodePassed,
    codeApplied = "",
    fromList
  ) => {
    if (payload) {
      // Check if previous discounts are removed by current discount
      let previousDiscountError = true
      oldDiscountCodePassed.forEach(oldCode => {
        const newCode = payload.discountCodes?.find(
          code =>
            code.discountCode?.obj?.code === oldCode.discountCode?.obj?.code
        )
        if (
          previousDiscountError &&
          oldCode.state === "MatchesCart" &&
          oldPromoCodePassed?.find(
            promo => promo.code === oldCode.discountCode?.obj?.code
          ) &&
          newCode?.state === "ApplicationStoppedByPreviousDiscount"
        ) {
          previousDiscountError = false
        }
      })
      if (!previousDiscountError) {
        setPromoError(i18n?.previousDiscountErr ?? "")
        updatePromoAnalytics(
          i18n?.previousDiscountErr ?? "",
          payload,
          promoCode?.trim().toLowerCase() || codeApplied?.trim().toLowerCase(),
          "apply",
          fromList
        )
        toastError(i18n?.previousDiscountErr ?? "")
      } else {
        const resCode =
          payload.discountCodes?.find(
            code =>
              code.discountCode?.obj?.code?.toLowerCase() ===
                promoCode?.trim().toLowerCase() ||
              code.discountCode?.obj?.code?.toLowerCase() ===
                codeApplied?.trim().toLowerCase()
          ) ?? {}

        // Check if applied code can be combined with existing discounts
        if (resCode.state === stopedByPreviousDiscount) {
          setPromoError(i18n?.combinePromoErr ?? "")
          updatePromoAnalytics(
            i18n?.combinePromoErr ?? "",
            payload,
            promoCode?.trim().toLowerCase() ||
              codeApplied?.trim().toLowerCase(),
            "apply",
            fromList
          )
        }
        // Check if applied code has reached the maximum number of times it can be applied
        else if (resCode.state === maxAppReached) {
          setPromoError(i18n?.maxAppReachedErr ?? "")
          updatePromoAnalytics(
            i18n?.maxAppReachedErr ?? "",
            payload,
            promoCode?.trim().toLowerCase() ||
              codeApplied?.trim().toLowerCase(),
            "apply",
            fromList
          )
          setPromo("")
        } else {
          const code = _get(resCode, "state", "")
          if (code.toLowerCase() === "doesnotmatchcart") {
            setPromoError(i18n?.promoNotApplicable ?? "")
            updatePromoAnalytics(
              i18n?.promoNotApplicable ?? "",
              payload,
              promoCode?.trim().toLowerCase() ||
                codeApplied?.trim().toLowerCase(),
              "apply",
              fromList
            )
            if (!promoCodesData.length) setPromo("")
          } else {
            updatePromoAnalytics(
              "success",
              payload,
              promoCode?.trim().toLowerCase() ||
                codeApplied?.trim().toLowerCase(),
              "apply",
              fromList
            )
          }
          // if (
          //   payload.discountCodes.length &&
          //   payload.discountCodes?.[0]?.state === "DoesNotMatchCart"
          // ) {
          //   const discountCode = payload.discountCodes?.[0]?.discountCode
          //   handleRemoveDiscount(discountCode?.id, discountCode?.obj?.code)
          // }
        }
      }
      dispatchCallback("taxCalculation")
    }
  }

  const updatePromoAnalytics = async (
    status,
    cartObj,
    promo,
    action,
    fromList
  ) => {
    const { adobeDataLayer: { getState } = {} } = window
    const page = (getState && getState("page")) || {}

    const eventInfo = {
      eventAction: `cart:landing page:${action} promo`,
      eventName: `cart:landing page:${action} promo`,
      eventType: "navigation",
      eventMsg:
        status === "success"
          ? action === "apply"
            ? "promocode applied successfully"
            : "promocode removed successfully"
          : status?.toLowerCase(),
      eventStatus: status === "success" ? "success" : "failure",
      internalLinkName: action,
      internalLinkPosition: "cart",
      internalLinkType: "cart:promo",
      internalLinkZoneName: "cart:landing page:order summary",
      internalLinkURL: "n/a",
      clickInternalLinks: "true",
      promoCode: promo?.toLowerCase(),
      ...(action === "apply" && {
        promoCodeSource: fromList ? "suggested" : "manual",
      }),
    }

    if (cartObj) {
      const { cart } = await calculateCartData(
        {
          cart: cartObj,
          i18n: {
            productDiscounts: i18n.productDiscounts || "Product Discounts",
          },
        },
        true
      )

      page.cart = cart
    }

    window.adobeDataLayer.push({
      event: "cmp:click",
      eventInfo,
      page,
    })
  }
  const handleMouseOverTooltip = type => {
    const eventInfo = {
      eventAction: `cart:landing page:estimated ${type} mouseover`,
      eventName: `cart:landing page:estimated ${type} mouseover`,
      eventType: "navigation",
      eventMsg: "n/a",
      eventStatus: "n/a",
      internalLinkName:
        type === "tax"
          ? "estimated tax info the sales tax listed here is only an estimate. final taxes will be calculated at checkout."
          : "estimated shipping info shipping method can be selected and final shipping costs will be calculated during checkout.",
      internalLinkPosition: "cart",
      internalLinkType: "cart:mouseover",
      internalLinkZoneName: "cart:landing page:order summary",
      internalLinkURL: "n/a",
      clickInternalLinks: "true",
    }

    const { adobeDataLayer: { getState } = {} } = window
    const page = (getState && getState("page")) || {}
    page.cart = props?.cart

    window.adobeDataLayer.push({
      event: "cmp:click",
      eventInfo,
      page,
    })
  }
  const promoCodesData = promoCodes.filter(e => {
    if (e.type !== "product" && e.type !== "no-code" && e.id) return e
  })

  const {
    shipping = "",
    shippingTooltip = "",
    oversizedFragileDelivery = "",
    oversizedDelivery = "",
    freightShipping = "",
    freightLabel = "",
    editCart = "",
  } = i18n

  if (shippingAllTotal.length) {
    const fromIndex = shippingAllTotal.findIndex(
      el => el?.name === freightShipping || el?.name === freightLabel
    )
    const toIndex = shippingAllTotal.length - 1
    shippingAllTotal.splice(
      toIndex,
      0,
      shippingAllTotal.splice(fromIndex, 1)[0]
    )
  }

  const isCartItems = cartItems.length

  const handleShippingClick = async () => {
    const { legalPageData } = await getConfig()
    window.open(legalPageData?.map?.shippingPolicy, "_blank", "noreferrer")
  }

  const handleCheckout = async () => {
    try {
      setCheckoutLoading(true)
      dispatch(setCardStatus(true))
      redirectCallback(true)
      const errorData = await inventoryCheck()
      const hasError = Object.keys(errorData)?.length > 0
      if (!hasError) {
        //rest prev buy-now flow
        window.localStorage.setItem("buy-now-init", false)
        window.localStorage.setItem("regular-checkout", true)
        window.localStorage.removeItem("buy-now-cart")
        window.localStorage.removeItem("active-cart")

        continueCheckoutEventInfo(checkoutUrl)
        window.location.href = checkoutUrl
      }
    } catch (e) {
      console.log(e)
    } finally {
      setCheckoutLoading(false)
      dispatch(setCardStatus(false))
    }
  }
  useImperativeHandle(ref, () => ({ handleCheckout }))

  const handleClearAbandonedCart = cb => {
    clearCartFromStore(sub)
    handleAbandonedCarts(!abandonedFlag)
    removeCustomerCartId()
    cb()
  }

  const handlePromoInputKeyPress = e => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleAddDiscount(e)
    }
  }

  const handleApplyPromoCode = async item => {
    if (promo) {
      const appliedPromoCode = item.isApplied
        ? item
        : promoCodesData?.find(
            p => p.code.toLowerCase() === promo.toLowerCase()
          )
      handleRemoveDiscount(
        appliedPromoCode?.id,
        appliedPromoCode?.code,
        item.isApplied ? null : item.code
      )
    } else {
      handleAddDiscount(item.code, true)
    }
  }
  const discountPercent = Math.round((totalPromotions / MRPsubtotal) * 100)
  const itemDiscountPercent = Math.round((productPromotions / subtotal) * 100)
  return (
    <div className="row no-gutters order-summary-wrapper sticky-order">
      <div className="col-12 pl-0 pr-0">
        <div className="order-summary ">
          <div className="order-summary__title" role="heading" aria-level="2">
            {i18n.orderSummary}
            {checkoutMode ? (
              <a href={cartPage} prefetch={false}>
                <p
                  tabIndex="0"
                  role="link"
                  aria-label={"Back to Cart"}
                  className="edit-cart-btn"
                  onClick={() =>
                    addAnalyticsEditCart(
                      window.location.origin.toLowerCase() + cartPage
                    )
                  }
                >
                  {editCart}
                </p>
              </a>
            ) : null}
          </div>
          <OrderSummaryItem
            value={isCartItems ? subtotal : "-"}
            label={i18n.subTotal}
            prefix={currencySign}
          />
          {Number(productPromotions) > 0 ? (
            <OrderSummaryItem
              label={`${itemDiscountPercent}% ${i18n.itemDiscount}`}
              value={productPromotions > 0 ? productPromotions * -1 : ""}
              prefix={currencySign}
            />
          ) : null}
          {Number(couponPromotions) ? (
            <OrderSummaryItem
              label={`${i18n.couponDiscount}`}
              value={couponPromotions > 0 ? couponPromotions * -1 : ""}
              prefix={currencySign}
            />
          ) : null}
          <OrderSummaryItem
            value={i18n.freeShipping}
            label={i18n.estimatedShipping}
            type="shipping"
            icon={TooltipIcon?.src}
            alt={`${i18n.shippingInfoAria} ${i18n.shippingTooltip}`}
            onMouseOver={handleMouseOverTooltip}
            tooltip={i18n.shippingTooltip}
            prefix={currencySign}
          />
          {isCartItems
            ? shippingAllTotal.map((item = {}, i) => {
                const { name = "", total = 0 } = item
                const isFreightShipping =
                  name === freightShipping || name === freightLabel

                const shippingName = !isFreightShipping
                  ? `${name} ${shipping}`
                  : oversizedFragileDelivery

                const totalAmount = Number(total) ? total : i18n.freeShipping
                const tooltip = !isFreightShipping
                  ? shippingTooltip
                  : oversizedDelivery

                return (
                  <React.Fragment key={i}>
                    <OrderSummaryItem
                      value={totalAmount}
                      label={shippingName}
                      icon={
                        !streetName && !checkoutMode ? TooltipIcon?.src : null
                      }
                      alt={`${i18n.shippingInfoAria} ${tooltip}`}
                      tooltip={tooltip}
                      onMouseOver={handleMouseOverTooltip}
                      type="shipping"
                      prefix={currencySign}
                    />
                  </React.Fragment>
                )
              })
            : null}

          <hr
            role="presentation"
            aria-hidden="true"
            className="order-summary__separator cart_custom_margin_hr"
          />

          <div
            className={`order-summary-mobile-sticky   ${
              !checkoutMode ? "is-sticky-load" : ""
            }`}
          >
            <OrderSummaryItem
              label={
                !isNullOrUndefined(taxInformation) ? (
                  <span className="order-summary__item--label-wrapper">
                    <span className="order-summary__item--label-title">
                      {" "}
                      {i18n.total}
                    </span>
                  </span>
                ) : (
                  i18n.total
                )
              }
              value={isCartItems ? estimatedTotal : "-"}
              lineBreak={false}
              prefix={currencySign}
              valueSize="large"
              customCls="estimated-total"
            />
            <div className="order-summary__item-hr">
              <hr
                role="presentation"
                aria-hidden="true"
                className="order-summary__separator cart_custom_margin_hr"
              />
            </div>
            <div className="order-summary__item--tax-container pb-10">
              <span className="order-summary__item--label tax-label">
                {" "}
                {i18n.tax}
              </span>
              <span className="order-summary__item--label-description">
                {" "}
                <span>{i18n.including}</span>
                <span className="order-summary__item--label-description--number-format">
                  <NumericFormat
                    value={taxInformation}
                    displayType={"text"}
                    thousandSeparator={true}
                    prefix={currencySign}
                    decimalScale={2}
                    fixedDecimalScale={true}
                    thousandsGroupStyle={"lakh"}
                  />
                </span>
                <span> {i18n.InTaxes}</span>
              </span>
            </div>
            {Number(totalPromotions) > 0 && discountPercent > 0 ? (
              <OrderSummaryItem
                label={`${discountPercent}% ${i18n.totalDiscount}`}
                value={totalPromotions > 0 ? totalPromotions * -1 : ""}
                prefix={currencySign}
              />
            ) : null}
            {/* {!isCheckoutPage && estimatedTotal ? (
              <div className="order-summary__item--no-cost-emi">
                {estimatedTotal > ESTIMATED_TOTAL_FOR_NO_COST_EMI
                  ? i18n.noCostEmiAvailable
                  : i18n.availNoCostEmi({
                      amountLeft: (
                        ESTIMATED_TOTAL_FOR_NO_COST_EMI - estimatedTotal
                      ).toFixed(2),
                    })}
              </div>
            ) : null} */}
            {checkoutMode ? (
              <OrderReview
                i18n={i18n}
                orderSummary
                cart={activeCart}
                legalLinks={legalLinks}
              />
            ) : null}
          </div>
        </div>
        {!checkoutMode && activeCart.cartState !== "ordered" && (
          <Button
            ariaLabel={i18n.continueCheckoutAria(
              cartItems?.length,
              `${currencySign}${estimatedTotal}`
            )}
            data-gbh-data-layer-custom={`{"cart":{"item":"n/a","price":"${subtotal}"}}`}
            className="gbh-data-layer checkout-btn-group"
            label={CHECKOUT_BUTTON_LABEL}
            ref={buttonRef}
            flexible
            type="primary"
            onClick={handleCheckout}
            disabled={disableCheckout}
            role="button"
            loading={checkoutLoading}
          />
        )}
        {/* {isPromoAllowed() ? ( */}
        <div className="cart-accordian">
          <Collapsible title={i18n.promocode} isOpenAccordian={true}>
            <div className="cart-accordion-section">
              <div className="cart-promo-code-section">
                {!isPaymentStep &&
                  promoCodesList
                    ?.sort((a, b) => b.isApplied - a.isApplied)
                    .map((item, i) => (
                      <div className="promo-code-list" key={i}>
                        <div className="cart-promo-code">
                          <div className="active-promo-name">
                            <div>
                              {item.code}
                              {item.isApplied ? (
                                <span className="promo-code__badge">
                                  APPLIED
                                </span>
                              ) : (
                                ""
                              )}
                            </div>
                            <div className="promo-code-description">
                              {item.description}
                            </div>
                          </div>
                          <p className="promo-right-section">
                            {item.isApplied ? (
                              <span className="active-promo-price">
                                {item.type !== "shipping" ? (
                                  <OrderSummaryItem
                                    value={Number(item.discount * -1)}
                                    prefix={currencySign}
                                  />
                                ) : (
                                  ""
                                )}
                              </span>
                            ) : (
                              ""
                            )}
                            {promoLoading === i ? (
                              <div
                                className="loading-spinner"
                                style={{ marginLeft: 20 }}
                              ></div>
                            ) : (
                              <span
                                tabIndex={0}
                                className={`order-summary__close ${
                                  promoLoading || status === "loading"
                                    ? "disabled"
                                    : ""
                                }`}
                                onClick={() => {
                                  handleApplyPromoCode(item)
                                  setPromoLoading(i)
                                }}
                              >
                                {item.isApplied ? "Remove" : i18n.apply}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    ))}
              </div>
              <div className="order-summary__input-group">
                <div className="">
                  <Input
                    id="promo-code-input"
                    customClass={`order-summary__input ${
                      promoError ? "--error" : ""
                    }`}
                    label={i18n.enterPromo}
                    type="text"
                    pattern="[a-zA-Z0-9]+"
                    placeholder={i18n.enterPromo}
                    value={promoCode}
                    onChange={e => setPromoCode(e.target.value)}
                    onKeyDown={handlePromoInputKeyPress}
                    onFocus={() => {
                      setPromoError("")
                    }}
                  />
                </div>
                <Button
                  role="button"
                  tabIndex="0"
                  label={i18n.apply}
                  size="small"
                  type="ghost"
                  disabled={status === "loading"}
                  onClick={handleAddDiscount}
                />
              </div>
              {promoError && (
                <div
                  tabIndex="0"
                  role="alert"
                  id="kf-cart-promo-error"
                  className="order-summary__promo-error"
                >
                  {promoError}
                </div>
              )}
            </div>
          </Collapsible>
        </div>
        {/* ) : null} */}
        {isAbandonedCart && activeCart.anonymousId === undefined ? (
          <RemoveAbandonedCartButton
            handleButtonClick={handleClearAbandonedCart}
            staticText={i18n}
            loadAbandonedCart={loadAbandonedCart}
          />
        ) : null}
      </div>
    </div>
  )
})

export default OrderSummary

const RemoveAbandonedCartButton = ({
  handleButtonClick,
  staticText,
  loadAbandonedCart,
}) => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const handleClick = () => {
    setShowConfirmationModal(true)
  }
  const confirmYes = () => {
    setIsSuccess(true)
  }

  const confirmNo = () => {
    setShowConfirmationModal(false)
  }

  const handleCloseConfirmation = () => {
    handleButtonClick(() => {
      setShowConfirmationModal(false)
      loadAbandonedCart(false)
    })
  }
  return (
    <div className="pt-30">
      <Button
        ariaLabel={staticText.removeAbandonedCart}
        className="gbh-data-layer removeAbandonedCart-btn px-0"
        label={staticText.removeAbandonedCart}
        flexible
        type="ghost"
        onClick={handleClick}
      />
      <Modal showModal={showConfirmationModal} hideCloseIcon>
        <div className="RemoveAbandonedCartConfirmationModal__wrapper">
          <div className="RemoveAbandonedCartConfirmationModal__title">
            {isSuccess
              ? staticText.removeAbandonedCartSuccess
              : staticText.removeAbandonedCartConfirm}
          </div>
          {isSuccess ? (
            <Button
              label={staticText.close}
              flexible
              onClick={handleCloseConfirmation}
            />
          ) : (
            <div className="RemoveAbandonedCartConfirmationModal__ctas">
              <Button label={staticText.yes} onClick={confirmYes} flexible />
              <Button
                label={staticText.no}
                type="ghost"
                onClick={confirmNo}
                flexible
              />
            </div>
          )}
        </div>
      </Modal>
    </div>
  )
}
